import { TrashIcon } from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'motion/react';
import { forwardRef, KeyboardEventHandler, memo, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { secondaryInput } from '@/styles/styles';
import { ClassNameComponent } from '@/utils/ClassNameComponent';
import { KeyboardKeys } from '@/utils/Keyboard';
import { errorMessage } from '@/utils/animations/commom';
import ConditionalRenderer from '../ConditionalRenderer';
import IconButton from '../buttons/IconButton';
import { OmitInputDefaultClassname } from '../dataInput/BaseInput';
import Label, { labelCssModifier, LabelProps } from './Label';

type Props = OmitInputDefaultClassname & {
  label?: LabelProps;
  className?: ClassNameComponent;
  error?: string | boolean;
  mask?: (value: string) => string;
  onClear?: () => void;
  testId?: string;
  allowDecimal?: boolean;
  clearWrapper?: ReactNode;
};

const SecondaryInput = forwardRef<HTMLInputElement, Props>(
  (props: Props, ref) => {
    const {
      id,
      label,
      className,
      error,
      mask,
      autoComplete = 'off',
      disabled,
      onClear,
      testId,
      type,
      onKeyDown,
      allowDecimal = false,
      clearWrapper,
      ...rest
    } = props;

    const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = e => {
      if (type === 'number') {
        const allowedKeys = [
          KeyboardKeys.Backspace,
          KeyboardKeys.Delete,
          KeyboardKeys.Tab,
          KeyboardKeys.Escape,
          KeyboardKeys.Enter,
          KeyboardKeys.ArrowLeft,
          KeyboardKeys.ArrowRight,
          KeyboardKeys.Home,
          KeyboardKeys.End,
        ];

        if (allowDecimal) {
          allowedKeys.push(KeyboardKeys.Decimal, KeyboardKeys.Comma);
        }

        const isNumber = /^[0-9]$/.test(e.key);
        const isAllowedKey = allowedKeys.includes(e.key as KeyboardKeys);

        if (!isNumber && !isAllowedKey) {
          e.preventDefault();
        }
      }

      if (mask) {
        const input = e.currentTarget;
        input.value = mask(input.value);
      }

      onKeyDown?.(e);
    };

    return (
      <div
        className={twMerge(
          'relative flex flex-col w-full',
          labelCssModifier(label),
        )}
      >
        <div
          className={twMerge(
            secondaryInput,
            'grow flex gap-1 min-h-8',
            'transition-all duration-200 ease-in-out ',
            error && !disabled && 'border-error focus-within:border-error',
            disabled && 'pointer-events-none disabled',
            className?.base,
          )}
        >
          <div className="flex items-center gap-1">
            <ConditionalRenderer condition={label}>
              {label && <Label label={label} htmlFor={id} />}
            </ConditionalRenderer>
            <AnimatePresence>
              {error && !disabled && (
                <motion.label
                  className="text-error text-[0.875rem] overflow-hidden max-h-5"
                  {...errorMessage}
                >
                  *
                </motion.label>
              )}
            </AnimatePresence>
          </div>
          <input
            ref={ref}
            {...rest}
            id={id}
            type={type}
            data-testid={testId}
            disabled={disabled}
            autoComplete={autoComplete}
            onKeyDown={handleKeyDown}
            className={twMerge(
              'grow focus:outline-hidden flex w-full bg-transparent',
              className?.input,
            )}
          />
          <ConditionalRenderer condition={onClear}>
            <IconButton
              type="button"
              className={className?.button}
              icon={
                <TrashIcon className="text-error w-5 h-5 stroke-[1.5] hover:text-error/50 transition-all duration-200 ease-in-out" />
              }
              onClick={onClear}
            />
          </ConditionalRenderer>
          <ConditionalRenderer condition={clearWrapper}>
            {clearWrapper}
          </ConditionalRenderer>
        </div>
      </div>
    );
  },
);

export default memo(SecondaryInput);
